import { useEffect } from "react";
import Link from "next/link";

import config from "config";
import Skeleton from "ui-components/Skeleton";
import FileUpload from "ui-components/Local-File-Upload";

import { useGetUserDetails } from "auth/auth-state";
import { useGetUploadData } from "modules/data-files/api";

interface Props {
  onComplete?: () => unknown;
  onError?: React.ReactNode;
}

const UploadFiles = ({ onComplete, onError }: Props) => {
  const userDetails = useGetUserDetails();

  const { createData: uploadData, uploadStatus } = useGetUploadData();
  const allComplete =
    Object.keys(uploadStatus).length >= 1 &&
    Object.values(uploadStatus).filter((s) => !s.completed || s.error)
      .length === 0;

  const hasErrors =
    Object.values(uploadStatus).filter((s) => s.error).length >= 1;
  const anyComplete =
    Object.values(uploadStatus).filter((s) => s.completed && !s.error).length >=
    1;

  useEffect(() => {
    if (allComplete && onComplete) {
      onComplete();
    }
  }, [allComplete, onComplete]);

  if (!userDetails) {
    return <Skeleton />;
  }

  const { usage } = userDetails;
  const dataAccessPlan =
    // @ts-ignore
    userDetails.access_plan || userDetails.data_access_plan;

  const { "file-count": allowedFileCount } = dataAccessPlan;
  const { data_files } = usage;
  const maxFiles = allowedFileCount >= 1 ? allowedFileCount - data_files : -1;
  if (allowedFileCount >= 1 && maxFiles <= 0) {
    return (
      <>
        <p className="text-center mt-4 text-xl">
          Your plan allows for {allowedFileCount.toLocaleString()} data files.
          You already have {data_files.toLocaleString()} data files!
        </p>
        {config.DEPLOY_ENV === "daisho-cloud" ? (
          <p className="text-center mt-4 text-xl">
            <Link href="/profile/billing">
              <a className="text-primary-main underline">Click here</a>
            </Link>{" "}
            to change your plan.
          </p>
        ) : (
          <p className="text-center mt-4 text-xl">
            Contact your administrator @ {config.ADMIN_CONTACT} for more
            details, and next steps.
          </p>
        )}
      </>
    );
  }

  return (
    <div>
      <div className="mt-4">
        <p className="text-2xl font-semibold">Select Files to Upload:</p>
        <p className="mb-2">
          We currently support CSV, Excel, SAS and Parquet Files.
        </p>
        <FileUpload
          uploadStatus={uploadStatus}
          handleSubmit={uploadData}
          maxFiles={maxFiles}
        />
        {hasErrors && anyComplete && onError}
      </div>
    </div>
  );
};

export default UploadFiles;
